/* Dropdown blocks share MainMenu's top-level opening, overlay and mobile drawer. */
if (!customElements.get('header-dropdown')) {
  customElements.define('header-dropdown', class extends HTMLElement {
    connectedCallback() {
      this.disclosure = this.querySelector('.header-dropdown');
      this.summary = this.disclosure.querySelector('summary');
      this.branches = [...this.querySelectorAll('[data-dropdown-branch]')];
      this.desktop = window.matchMedia('(min-width: 1400px)');
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.addEventListener('click', this.handleClick.bind(this), options);
      this.addEventListener('keydown', this.handleKeydown.bind(this), options);
      // MainMenu also handles Escape on keyup; keep one keypress at one menu level.
      this.addEventListener('keyup', (event) => {
        if (event.key === 'Escape') event.stopPropagation();
      }, options);
      this.disclosure.addEventListener('toggle', () => {
        if (!this.disclosure.open) this.closeBranches();
      }, options);
      this.branches.forEach((branch) => {
        branch.addEventListener('mouseenter', () => {
          if (this.desktop.matches && window.matchMedia('(hover: hover)').matches) this.openBranch(branch);
        }, options);
      });
      // Direct category links have no branch to replace a previously opened flyout.
      this.querySelectorAll('.child-nav--dropdown > li > .header-dropdown__category').forEach((link) => {
        link.addEventListener('mouseenter', () => {
          if (this.desktop.matches && window.matchMedia('(hover: hover)').matches) this.closeBranches();
        }, options);
      });
      window.addEventListener('resize', this.handleResize.bind(this), options);
      window.addEventListener('scroll', () => {
        this.branches.filter(branch => branch.open).forEach(branch => this.positionFlyout(branch));
      }, { ...options, capture: true, passive: true });
      const section = this.closest('.shopify-section');
      if (section) {
        section.addEventListener('shopify:block:select', (event) => {
          if (event.target === this.disclosure) {
            event.stopPropagation();
            this.open();
          }
        }, options);
        section.addEventListener('shopify:block:deselect', (event) => {
          if (event.target === this.disclosure) {
            event.stopPropagation();
            this.close();
          }
        }, options);
      }
    }

    disconnectedCallback() { this.events.abort(); }

    open() {
      const menu = this.closest('main-menu');
      if (!menu || typeof menu.openMenuFromMouseEnter !== 'function') return;
      menu.openMenuFromMouseEnter(this.summary);
      menu.childNavOpen = true;
      if (!this.desktop.matches) menu.querySelector('.main-menu__content')?.classList.add('main-menu__content--no-focus');
    }

    close() {
      const menu = this.closest('main-menu');
      this.closeBranches();
      menu.close(this.disclosure, false);
      menu.toggleOverlay(false);
      menu.childNavOpen = false;
      menu.querySelector('.main-menu__content')?.classList.remove('main-menu__content--no-focus');
    }

    closeBranches(except) {
      this.branches.forEach((branch) => {
        if (branch !== except) {
          branch.open = false;
          branch.querySelector('.main-nav__grandchild').removeAttribute('style');
        }
      });
    }

    openBranch(branch) {
      this.closeBranches(branch);
      branch.open = true;
      this.positionFlyout(branch);
    }

    handleClick(event) {
      // Keep real page links usable on touch screens and out of MainMenu's toggle handler.
      const anchor = event.target.closest('a');
      if (anchor && anchor.getAttribute('href') !== '#' && !event.target.closest('.arrow-down-icon')) {
        event.stopPropagation();
        return;
      }
      const back = event.target.closest('.js-back');
      const summary = event.target.closest('summary');
      if (!back && !summary) return;
      event.preventDefault();
      event.stopPropagation();
      if (back) {
        this.close();
        this.summary.focus();
      } else if (summary === this.summary) {
        if (this.disclosure.open) this.close();
        else this.open();
      } else {
        const branch = summary.closest('[data-dropdown-branch]');
        if (branch.open) branch.open = false;
        else this.openBranch(branch);
      }
    }

    handleKeydown(event) {
      const branch = event.target.closest('[data-dropdown-branch]');
      const rtl = document.dir === 'rtl';
      if (branch && event.key === (rtl ? 'ArrowLeft' : 'ArrowRight') && this.desktop.matches) {
        event.preventDefault();
        this.openBranch(branch);
        branch.querySelector('.main-nav__grandchild a')?.focus();
      } else if (event.key === 'Escape' || (branch && this.desktop.matches && event.key === (rtl ? 'ArrowRight' : 'ArrowLeft'))) {
        event.preventDefault();
        event.stopPropagation();
        if (branch && branch.open) {
          branch.open = false;
          branch.querySelector('summary').focus();
        } else {
          this.close();
          this.summary.focus();
        }
      }
    }

    handleResize() {
      this.closeBranches();
    }

    positionFlyout(branch) {
      if (!this.desktop.matches || !branch.open) return;
      const row = branch.querySelector('summary').getBoundingClientRect();
      const panel = branch.querySelector('.main-nav__grandchild');
      const container = this.querySelector('.header-dropdown__panel').getBoundingClientRect();
      if (row.bottom < container.top || row.top > container.bottom) {
        branch.open = false;
        return;
      }
      panel.style.left = '0px';
      panel.style.top = '0px';
      const box = panel.getBoundingClientRect();
      const edge = 12;
      const rtl = document.dir === 'rtl';
      let left = rtl ? row.left - box.width : row.right;
      if (!rtl && left + box.width > window.innerWidth - edge) left = row.left - box.width;
      if (rtl && left < edge) left = row.right;
      left = Math.max(edge, Math.min(left, window.innerWidth - box.width - edge));
      // Preserve the parent row alignment; long lists scroll within the remaining space.
      const top = Math.max(edge, row.top);
      panel.style.maxHeight = `${Math.max(0, window.innerHeight - top - edge)}px`;
      // Account for transformed ancestors (e.g. the sticky header's translateY).
      panel.style.left = `${left - box.left}px`;
      panel.style.top = `${top - box.top}px`;
    }
  });
}
